/**
 * GIWAHS Adaptive Journey — Persistence Adapter Contract (AJIP-16.02.004 · WS2)
 * ---------------------------------------------------------------------
 * The adapter contract is the architectural asset (Founder Ruling P-01).
 * The Persistence Framework depends only on this contract. It never
 * inspects an adapter's internals and never knows where persistence
 * ultimately lives (Additional Founder Directive).
 *
 * Contract methods (all async unless noted; each returns a Promise)
 *   get(key)                     → serialised string | null
 *   set(key, value, meta?)       → void
 *   update(key, patcher, meta?)  → serialised string (post-update)
 *   remove(key)                  → boolean
 *   list(prefix?)                → string[]  (keys)
 *   clearExpired(nowMs, prefix?) → number    (records removed)
 *   healthCheck()                → { ok: boolean, adapter: string, details?: object }
 *
 * Governance
 *   * Adapters MUST NOT interpret the value string. It is opaque JSON.
 *   * Adapters MUST fail with a subclass of PersistenceError when they
 *     encounter storage-layer faults; never let a raw exception escape.
 *   * Adapters MUST be side-effect free at import time.
 *   * Adapters MUST NOT persist secrets or credentials — that
 *     responsibility rests with PersistedRecord's serialiser upstream.
 *
 * Validator
 *   assertAdapter(candidate) throws AdapterContractError if the
 *   candidate does not conform. Called by PersistenceService when an
 *   adapter is bound. This is the ONLY compatibility surface between
 *   the framework and any adapter.
 */
import { AdapterContractError } from './PersistenceErrors';

const CONTRACT_NAME = 'PersistenceAdapter';
const REQUIRED_METHODS = Object.freeze([
  'get',
  'set',
  'update',
  'remove',
  'list',
  'clearExpired',
  'healthCheck',
]);

const assertAdapter = (candidate) => {
  if (!candidate || typeof candidate !== 'object') {
    throw new AdapterContractError(`${CONTRACT_NAME}: candidate must be a non-null object`);
  }
  for (const m of REQUIRED_METHODS) {
    if (typeof candidate[m] !== 'function') {
      throw new AdapterContractError(`${CONTRACT_NAME}: missing required method '${m}'`);
    }
  }
  if (typeof candidate.name !== 'string' || candidate.name.length === 0) {
    throw new AdapterContractError(`${CONTRACT_NAME}: adapter must expose a non-empty 'name' string`);
  }
  return true;
};

export { CONTRACT_NAME, REQUIRED_METHODS, assertAdapter };
